import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Badge, Button, ButtonGroup, Image, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import StandardModal from 'components/general/defaultModals/StandardModal';
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar';

const normalizeAcl = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'officer' || normalized === 'member') {
    return 'Member';
  }
  return 'Guest';
};

const parseDateBucket = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const parseHalfYearBucket = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  const y = d.getFullYear();
  const m = d.getMonth(); // 0..11
  if (m >= 3 && m <= 8) {
    // April (3) to September (8) -> Period ending Sep 30
    return `${y}-09`;
  } else if (m >= 9) {
    // October (9) to December (11) -> Period ending Mar 31 of next year
    return `${y + 1}-03`;
  } else {
    // January (0) to March (2) -> Period ending Mar 31 of current year
    return `${y}-03`;
  }
};

const formatPaidDateDisplay = (dateStr) => {
  if (!dateStr || dateStr === 'Unset / Unpaid') return 'Unset / Unpaid';
  return dateStr.replace(/March/g, 'Mar').replace(/September/g, 'Sep');
};

const formatPaidDateHeader = (dateStr) => {
  if (!dateStr || dateStr === 'Unset / Unpaid') return 'Unset / Unpaid';
  const shortFormatted = dateStr.replace(/March/g, 'Mar').replace(/September/g, 'Sep');
  return shortFormatted.toLowerCase();
};

const formatBucketLabel = (bucketKey) => {
  if (bucketKey === 'Unknown') return 'Unknown';
  const [year, month] = bucketKey.split('-');
  const dateObj = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
  return dateObj.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const formatHalfYearBucketLabel = (bucketKey) => {
  if (bucketKey === 'Unknown') return 'Unknown';
  const [yearStr, monthStr] = bucketKey.split('-');
  const year = parseInt(yearStr, 10);
  const yrShort = String(year).slice(-2);
  if (monthStr === '03') {
    return `Oct-Mar '${yrShort}`;
  }
  return `Apr-Sep '${yrShort}`;
};

export default function UsersStatsModal({ show, handleClose, userList = [], navigationParent = null }) {
  const navigate = useNavigate();
  const [chartMode, setChartMode] = useState('monthly'); // 'monthly' | 'cumulative'
  const [theme, setTheme] = useState(document.body.getAttribute('data-theme') || 'light');
  const [selectedCategory, setSelectedCategory] = useState(null); // { title: string, users: Array }

  const checkIsSmallPortrait = () => {
    if (typeof window === 'undefined') return false;
    const isPortraitMedia = window.matchMedia('(max-width: 768px) and (orientation: portrait)').matches;
    const isPortraitDim = window.innerWidth <= 768 && window.innerHeight > window.innerWidth;
    return isPortraitMedia || isPortraitDim;
  };

  const [isSmallPortrait, setIsSmallPortrait] = useState(checkIsSmallPortrait);

  useEffect(() => {
    setTheme(document.body.getAttribute('data-theme') || 'light');
    const observer = new MutationObserver(() => {
      setTheme(document.body.getAttribute('data-theme') || 'light');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const handleRequestLandscape = async () => {
    try {
      if (window.screen && window.screen.orientation && typeof window.screen.orientation.lock === 'function') {
        await window.screen.orientation.lock('landscape').catch(() => {});
      }
    } catch {
      // Ignore if unsupported or denied by browser policy
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallPortrait(checkIsSmallPortrait());
    };

    handleResize();

    const mediaQuery = window.matchMedia('(max-width: 768px) and (orientation: portrait)');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleResize);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleResize);
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleResize);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleResize);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const isDark = theme === 'dark';

  // Statistics Computations
  const stats = useMemo(() => {
    let totalUsers = userList.length;
    let totalMembers = 0; // Officers + Members
    let totalGuests = 0;
    let totalConvertedGuests = 0;

    const membersList = [];
    const guestsList = [];
    const convertedGuestsList = [];

    const paidUntilMap = {};
    const paidUntilUsersMap = {};

    // Month buckets for all users
    const allUsersByMonth = {};
    // Month buckets for members (officers + members)
    const membersByMonth = {};
    // 6-month (half-year) buckets for former members now converted to guests
    const convertedGuestsByHalfYear = {};

    userList.forEach((u) => {
      const isMem = normalizeAcl(u.acl) === 'Member';
      const isConvertedGuest = !isMem && Boolean(
        String(u.memberNo || u.toastmastersId || '').trim() ||
        String(u.paidUntil || '').trim() ||
        (Array.isArray(u.clubRoles) && u.clubRoles.length > 0)
      );

      if (isMem) {
        totalMembers++;
        membersList.push(u);
        const paidKey = String(u.paidUntil || '').trim() || 'Unset / Unpaid';
        paidUntilMap[paidKey] = (paidUntilMap[paidKey] || 0) + 1;
        if (!paidUntilUsersMap[paidKey]) paidUntilUsersMap[paidKey] = [];
        paidUntilUsersMap[paidKey].push(u);
      } else {
        totalGuests++;
        guestsList.push(u);
        if (isConvertedGuest) {
          totalConvertedGuests++;
          convertedGuestsList.push(u);
        }
      }

      const bucket = parseDateBucket(u.createdAt);
      allUsersByMonth[bucket] = (allUsersByMonth[bucket] || 0) + 1;
      if (isMem) {
        membersByMonth[bucket] = (membersByMonth[bucket] || 0) + 1;
      }
      if (isConvertedGuest) {
        const hyBucket = parseHalfYearBucket(u.updatedAt || u.createdAt);
        convertedGuestsByHalfYear[hyBucket] = (convertedGuestsByHalfYear[hyBucket] || 0) + 1;
      }
    });

    // Collect and sort month keys (excluding 'Unknown' for timeline, add 'Unknown' at end if needed)
    const sortedBuckets = Object.keys(allUsersByMonth)
      .filter((b) => b !== 'Unknown')
      .sort();

    // Fill all missing monthly buckets between first and last date
    const filledMonthBuckets = [];
    if (sortedBuckets.length > 0) {
      const [startYr, startMo] = sortedBuckets[0].split('-').map(Number);
      const [endYr, endMo] = sortedBuckets[sortedBuckets.length - 1].split('-').map(Number);
      let curYr = startYr;
      let curMo = startMo;
      while (curYr < endYr || (curYr === endYr && curMo <= endMo)) {
        const key = `${curYr}-${String(curMo).padStart(2, '0')}`;
        filledMonthBuckets.push(key);
        curMo++;
        if (curMo > 12) {
          curMo = 1;
          curYr++;
        }
      }
    }

    // Generate monthly timeline series
    let cumAll = 0;
    let cumMembers = 0;
    const timeline = filledMonthBuckets.map((b) => {
      const allCount = allUsersByMonth[b] || 0;
      const memCount = membersByMonth[b] || 0;
      cumAll += allCount;
      cumMembers += memCount;
      return {
        bucket: b,
        label: formatBucketLabel(b),
        allJoined: allCount,
        membersTransformed: memCount,
        cumAll,
        cumMembers,
      };
    });

    if (allUsersByMonth['Unknown']) {
      timeline.push({
        bucket: 'Unknown',
        label: 'Unknown',
        allJoined: allUsersByMonth['Unknown'],
        membersTransformed: membersByMonth['Unknown'] || 0,
        cumAll: cumAll + (allUsersByMonth['Unknown'] || 0),
        cumMembers: cumMembers + (membersByMonth['Unknown'] || 0),
      });
    }

    // Determine 6-month period range based on monthly range or converted data
    const sortedHalfYearBuckets = Object.keys(convertedGuestsByHalfYear)
      .filter((b) => b !== 'Unknown')
      .sort();

    const allRangeBuckets = [...sortedBuckets, ...sortedHalfYearBuckets].sort();
    const filledHalfYearBuckets = [];

    if (allRangeBuckets.length > 0) {
      const firstHY = parseHalfYearBucket(allRangeBuckets[0] + '-01');
      const lastHY = parseHalfYearBucket(allRangeBuckets[allRangeBuckets.length - 1] + '-01');
      const [startYr, startMo] = firstHY.split('-').map(Number);
      const [endYr, endMo] = lastHY.split('-').map(Number);

      let curYr = startYr;
      let curMo = startMo;
      while (curYr < endYr || (curYr === endYr && curMo <= endMo)) {
        const key = `${curYr}-${String(curMo).padStart(2, '0')}`;
        filledHalfYearBuckets.push(key);
        if (curMo === 3) {
          curMo = 9;
        } else {
          curMo = 3;
          curYr++;
        }
      }
    }

    let cumConverted = 0;
    const convertedTimeline = filledHalfYearBuckets.map((b) => {
      const count = convertedGuestsByHalfYear[b] || 0;
      cumConverted += count;
      return {
        bucket: b,
        label: formatHalfYearBucketLabel(b),
        count,
        cumConverted,
      };
    });

    if (convertedGuestsByHalfYear['Unknown']) {
      convertedTimeline.push({
        bucket: 'Unknown',
        label: 'Unknown',
        count: convertedGuestsByHalfYear['Unknown'],
        cumConverted: cumConverted + convertedGuestsByHalfYear['Unknown'],
      });
    }

    return {
      totalUsers,
      totalMembers,
      totalGuests,
      totalConvertedGuests,
      membersList,
      guestsList,
      convertedGuestsList,
      paidUntilMap,
      paidUntilUsersMap,
      timeline,
      convertedTimeline,
    };
  }, [userList]);

  // SVG Chart Dimensions
  const chartHeight = 250;
  const chartWidth = 900;
  const padding = { top: 30, right: 30, bottom: 80, left: 45 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Theme-aware SVG Colors
  const gridLineColor = isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)';
  const axisTextColor = isDark ? '#94a3b8' : '#475569';
  const barAllFill = isDark ? '#3b82f6' : '#004165';
  const textAllFill = isDark ? '#93c5fd' : '#004165';
  const barMemFill = isDark ? '#f59e0b' : '#772432';
  const textMemFill = isDark ? '#fde68a' : '#772432';
  const barConvertedFill = isDark ? '#ef4444' : '#b91c1c';
  const textConvertedFill = isDark ? '#fca5a5' : '#991b1b';

  // Max values for chart scales
  const maxAllJoined = useMemo(() => {
    const vals = stats.timeline.map((t) => (chartMode === 'cumulative' ? t.cumAll : t.allJoined));
    return Math.max(...vals, 1);
  }, [stats.timeline, chartMode]);

  const maxMembersTransformed = useMemo(() => {
    const vals = stats.timeline.map((t) => (chartMode === 'cumulative' ? t.cumMembers : t.membersTransformed));
    return Math.max(...vals, 1);
  }, [stats.timeline, chartMode]);

  const maxMembersConverted = useMemo(() => {
    const vals = stats.convertedTimeline.map((t) => (chartMode === 'cumulative' ? t.cumConverted : t.count));
    return Math.max(...vals, 1);
  }, [stats.convertedTimeline, chartMode]);

  const renderRotateNotice = () => (
    <div
      className="text-center py-4 my-2 d-flex flex-column align-items-center justify-content-center"
      onClick={handleRequestLandscape}
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer' }}
    >
      <i className="bi bi-phone-landscape fs-2 text-warning mb-2 rotate-phone-icon" />
      <span className="fw-semibold" style={{ color: 'var(--foreground)', fontSize: '0.85rem' }}>
        rotate your device in landscape mode
      </span>
    </div>
  );

  const renderModalContent = () => (
    <div className="users-stats-content p-2" data-bs-theme={isDark ? 'dark' : 'light'}>
      {/* Overview Cards */}
      <h6 className="text-uppercase text-muted fw-bold mb-2" style={{ fontSize: '0.65rem' }}>Membership Overview</h6>
      <Row className="g-2 mb-3">
        <Col xs={12} sm={4}>
          <Card
            className="meetings-mock-card-shell h-100 p-2 p-sm-3 shadow-sm"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedCategory({ title: 'Total Members', users: stats.membersList })}
          >
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="text-muted fw-semibold" style={{ fontSize: '0.65rem' }}>Total Members</span>
              <Badge bg="primary" pill style={{ fontSize: '0.65rem' }}><i className="bi bi-people-fill me-1" />Members</Badge>
            </div>
            <div className="fs-4 fw-bold" style={{ color: isDark ? '#ffd066' : 'var(--tm-maroon, #772432)' }}>
              {stats.totalMembers}
            </div>
            <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
              {stats.totalUsers > 0 ? `${Math.round((stats.totalMembers / stats.totalUsers) * 100)}% of total users` : '0%'}
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={4}>
          <Card
            className="meetings-mock-card-shell h-100 p-2 p-sm-3 shadow-sm"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedCategory({ title: 'Guests Total', users: stats.guestsList })}
          >
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="text-muted fw-semibold" style={{ fontSize: '0.65rem' }}>Guests Total</span>
              <Badge bg="secondary" pill style={{ fontSize: '0.65rem' }}><i className="bi bi-person me-1" />Guests</Badge>
            </div>
            <div className="fs-4 fw-bold" style={{ color: 'var(--foreground)' }}>
              {stats.totalGuests}
            </div>
            <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
              {stats.totalUsers > 0 ? `${Math.round((stats.totalGuests / stats.totalUsers) * 100)}% of total users` : '0%'}
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={4}>
          <Card
            className="meetings-mock-card-shell h-100 p-2 p-sm-3 shadow-sm"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedCategory({ title: 'All Users', users: userList })}
          >
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="text-muted fw-semibold" style={{ fontSize: '0.65rem' }}>All Users</span>
              <Badge bg="info" pill style={{ fontSize: '0.65rem' }}><i className="bi bi-person-badge-fill me-1" />Total</Badge>
            </div>
            <div className="fs-4 fw-bold" style={{ color: isDark ? '#9ad9ff' : 'var(--tm-blue, #004165)' }}>
              {stats.totalUsers}
            </div>
            <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>Members + Guests combined</div>
          </Card>
        </Col>
      </Row>

      {/* Paid Until Breakdown */}
      <h6 className="text-uppercase text-muted fw-bold mb-2" style={{ fontSize: '0.65rem' }}>Members Paid Until Breakdown</h6>
      <Row className="g-2 mb-3">
        {Object.keys(stats.paidUntilMap).length === 0 ? (
          <Col xs={12}>
            <Card className="meetings-mock-card-shell p-2 text-center">
              <span className="text-muted" style={{ fontSize: '0.65rem' }}>No member paid info available.</span>
            </Card>
          </Col>
        ) : (
          Object.entries(stats.paidUntilMap).map(([paidDate, count]) => {
            const isUnpaid = paidDate === 'Unset / Unpaid';
            const pct = stats.totalMembers > 0 ? Math.round((count / stats.totalMembers) * 100) : 0;
            const users = stats.paidUntilUsersMap[paidDate] || [];
            const displayLabel = formatPaidDateDisplay(paidDate);
            const headerLabel = isUnpaid ? 'Unset / Unpaid' : formatPaidDateHeader(paidDate);
            return (
              <Col xs={12} sm={6} md={6} key={paidDate}>
                <Card
                  className="meetings-mock-card-shell p-2 p-sm-3 h-100"
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedCategory({ title: headerLabel, users })}
                >
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-semibold text-truncate me-2" style={{ fontSize: '0.65rem' }} title={displayLabel}>
                      <i className={`bi ${isUnpaid ? 'bi-exclamation-circle text-warning' : 'bi-calendar-check text-success'} me-1`} />
                      {displayLabel}
                    </span>
                    <Badge bg={isUnpaid ? 'warning' : 'success'} style={{ fontSize: '0.65rem' }} className="px-2 py-1">
                      {count} {count === 1 ? 'member' : 'members'}
                    </Badge>
                  </div>
                  <div className="progress mt-1" style={{ height: '5px' }}>
                    <div
                      className={`progress-bar ${isUnpaid ? 'bg-warning' : 'bg-success'}`}
                      role="progressbar"
                      style={{ width: `${pct}%` }}
                      aria-valuenow={pct}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                  <div className="text-end text-muted mt-1" style={{ fontSize: '0.75rem' }}>{pct}% of members</div>
                </Card>
              </Col>
            );
          })
        )}
      </Row>

      {/* Chart Controls & Graph Section */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="text-uppercase text-muted small fw-bold mb-0">Growth & Conversion Analytics Over Time</h6>
        <ButtonGroup size="sm">
          <Button
            variant={chartMode === 'monthly' ? (isDark ? 'primary' : 'dark') : (isDark ? 'outline-secondary' : 'outline-dark')}
            onClick={() => setChartMode('monthly')}
          >
            Monthly Additions
          </Button>
          <Button
            variant={chartMode === 'cumulative' ? (isDark ? 'primary' : 'dark') : (isDark ? 'outline-secondary' : 'outline-dark')}
            onClick={() => setChartMode('cumulative')}
          >
            Cumulative Total
          </Button>
        </ButtonGroup>
      </div>

      {stats.timeline.length === 0 ? (
        <Card className="meetings-mock-card-shell p-4 text-center mb-3">
          <span className="text-muted">No date statistics available yet.</span>
        </Card>
      ) : (
        <Row className="g-3">
          {/* Graph 1: Joined Every Month */}
          <Col xs={12}>
            <Card className="meetings-mock-card-shell p-3 mb-2" style={{ maxWidth: '100%' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong className="fs-6" style={{ color: textAllFill }}>
                  <i className="bi bi-graph-up me-1" />
                  {chartMode === 'cumulative' ? 'Cumulative Users Joined' : 'Users Joined per Month'}
                </strong>
                <Badge bg="secondary">{stats.timeline.length} time periods</Badge>
              </div>
              <div className="w-100 overflow-hidden">
                {isSmallPortrait ? renderRotateNotice() : (
                  <svg
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    preserveAspectRatio="none"
                    style={{ width: '100%', height: '250px', display: 'block' }}
                  >
                    {/* Grid Lines */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                      const y = padding.top + innerHeight * (1 - ratio);
                      const val = Math.round(maxAllJoined * ratio);
                      return (
                        <g key={idx}>
                          <line
                            x1={padding.left}
                            y1={y}
                            x2={chartWidth - padding.right}
                            y2={y}
                            stroke={gridLineColor}
                            strokeDasharray="3 3"
                          />
                          <text
                            x={padding.left - 5}
                            y={y + 4}
                            fill={axisTextColor}
                            fontSize="12"
                            textAnchor="end"
                          >
                            {val}
                          </text>
                        </g>
                      );
                    })}

                    {/* Bars & Labels */}
                    {stats.timeline.map((item, idx) => {
                      const val = chartMode === 'cumulative' ? item.cumAll : item.allJoined;
                      const countRatio = val / maxAllJoined;
                      const barWidth = Math.max(8, (innerWidth / stats.timeline.length) * 0.6);
                      const step = innerWidth / stats.timeline.length;
                      const x = padding.left + idx * step + (step - barWidth) / 2;
                      const barHeight = innerHeight * countRatio;
                      const y = padding.top + innerHeight - barHeight;
                      const labelY = padding.top + innerHeight + 8;

                      return (
                        <g key={item.bucket} className="chart-bar-group">
                          <rect
                            x={x}
                            y={y}
                            width={barWidth}
                            height={barHeight}
                            fill={barAllFill}
                            rx={3}
                          >
                            <title>{`${item.label}: ${val} joined`}</title>
                          </rect>
                          {val > 0 && (
                            <text
                              x={x + barWidth / 2}
                              y={y - 5}
                              fill={textAllFill}
                              fontSize="12"
                              fontWeight="bold"
                              textAnchor="middle"
                            >
                              {val}
                            </text>
                          )}
                          <text
                            x={x + barWidth / 2}
                            y={labelY}
                            transform={`rotate(-90, ${x + barWidth / 2}, ${labelY})`}
                            fill={axisTextColor}
                            fontSize="11"
                            textAnchor="end"
                          >
                            {item.label}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                )}
              </div>
            </Card>
          </Col>

          {/* Graph 2: Guests Transformed to Members/Officers */}
          <Col xs={12}>
            <Card className="meetings-mock-card-shell p-3 mb-2" style={{ maxWidth: '100%' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong className="fs-6" style={{ color: textMemFill }}>
                  <i className="bi bi-star-fill me-1" />
                  {chartMode === 'cumulative' ? 'Cumulative Members Transformed' : 'Members Joined per Month'}
                </strong>
                <Badge bg="secondary">Members Only</Badge>
              </div>
              <div className="w-100 overflow-hidden">
                {isSmallPortrait ? renderRotateNotice() : (
                  <svg
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    preserveAspectRatio="none"
                    style={{ width: '100%', height: '250px', display: 'block' }}
                  >
                    {/* Grid Lines */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                      const y = padding.top + innerHeight * (1 - ratio);
                      const val = Math.round(maxMembersTransformed * ratio);
                      return (
                        <g key={idx}>
                          <line
                            x1={padding.left}
                            y1={y}
                            x2={chartWidth - padding.right}
                            y2={y}
                            stroke={gridLineColor}
                            strokeDasharray="3 3"
                          />
                          <text
                            x={padding.left - 5}
                            y={y + 4}
                            fill={axisTextColor}
                            fontSize="12"
                            textAnchor="end"
                          >
                            {val}
                          </text>
                        </g>
                      );
                    })}

                    {/* Bars & Labels */}
                    {stats.timeline.map((item, idx) => {
                      const val = chartMode === 'cumulative' ? item.cumMembers : item.membersTransformed;
                      const countRatio = val / maxMembersTransformed;
                      const barWidth = Math.max(8, (innerWidth / stats.timeline.length) * 0.6);
                      const step = innerWidth / stats.timeline.length;
                      const x = padding.left + idx * step + (step - barWidth) / 2;
                      const barHeight = innerHeight * countRatio;
                      const y = padding.top + innerHeight - barHeight;
                      const labelY = padding.top + innerHeight + 8;

                      return (
                        <g key={item.bucket} className="chart-bar-group">
                          <rect
                            x={x}
                            y={y}
                            width={barWidth}
                            height={barHeight}
                            fill={barMemFill}
                            rx={3}
                          >
                            <title>{`${item.label}: ${val} members`}</title>
                          </rect>
                          {val > 0 && (
                            <text
                              x={x + barWidth / 2}
                              y={y - 5}
                              fill={textMemFill}
                              fontSize="12"
                              fontWeight="bold"
                              textAnchor="middle"
                            >
                              {val}
                            </text>
                          )}
                          <text
                            x={x + barWidth / 2}
                            y={labelY}
                            transform={`rotate(-90, ${x + barWidth / 2}, ${labelY})`}
                            fill={axisTextColor}
                            fontSize="11"
                            textAnchor="end"
                          >
                            {item.label}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                )}
              </div>
            </Card>
          </Col>

          {/* Graph 3: Members converted back to Guests (6-Month Periods) */}
          <Col xs={12}>
            <Card className="meetings-mock-card-shell p-3" style={{ maxWidth: '100%' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong className="fs-6" style={{ color: textConvertedFill }}>
                  <i className="bi bi-person-down me-1" />
                  {chartMode === 'cumulative' ? 'Cumulative Members Converted to Guests' : 'Members Converted to Guests per 6 Months'}
                </strong>
                <Badge bg="danger" style={{ fontSize: '0.7rem' }}>Converted to Guests</Badge>
              </div>
              <div className="w-100 overflow-hidden">
                {isSmallPortrait ? renderRotateNotice() : (
                  <svg
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    preserveAspectRatio="none"
                    style={{ width: '100%', height: '250px', display: 'block' }}
                  >
                    {/* Grid Lines */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                      const y = padding.top + innerHeight * (1 - ratio);
                      const val = Math.round(maxMembersConverted * ratio);
                      return (
                        <g key={idx}>
                          <line
                            x1={padding.left}
                            y1={y}
                            x2={chartWidth - padding.right}
                            y2={y}
                            stroke={gridLineColor}
                            strokeDasharray="3 3"
                          />
                          <text
                            x={padding.left - 5}
                            y={y + 4}
                            fill={axisTextColor}
                            fontSize="12"
                            textAnchor="end"
                          >
                            {val}
                          </text>
                        </g>
                      );
                    })}

                    {/* Bars & Labels */}
                    {stats.convertedTimeline.map((item, idx) => {
                      const val = chartMode === 'cumulative' ? item.cumConverted : item.count;
                      const countRatio = val / maxMembersConverted;
                      const numPeriods = stats.convertedTimeline.length || 1;
                      const barWidth = Math.max(12, (innerWidth / numPeriods) * 0.5);
                      const step = innerWidth / numPeriods;
                      const x = padding.left + idx * step + (step - barWidth) / 2;
                      const barHeight = innerHeight * countRatio;
                      const y = padding.top + innerHeight - barHeight;
                      const labelY = padding.top + innerHeight + 8;

                      return (
                        <g key={item.bucket} className="chart-bar-group">
                          <rect
                            x={x}
                            y={y}
                            width={barWidth}
                            height={barHeight}
                            fill={barConvertedFill}
                            rx={3}
                          >
                            <title>{`${item.label}: ${val} converted to guests`}</title>
                          </rect>
                          {val > 0 && (
                            <text
                              x={x + barWidth / 2}
                              y={y - 5}
                              fill={textConvertedFill}
                              fontSize="12"
                              fontWeight="bold"
                              textAnchor="middle"
                            >
                              {val}
                            </text>
                          )}
                          <text
                            x={x + barWidth / 2}
                            y={labelY}
                            transform={`rotate(-90, ${x + barWidth / 2}, ${labelY})`}
                            fill={axisTextColor}
                            fontSize="11"
                            textAnchor="end"
                          >
                            {item.label}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );

  const handleUserClick = (u) => {
    const userId = String(u?._id || u?.userId || u?.tmAuthUserId || '');
    if (userId) {
      if (handleClose) handleClose();
      setSelectedCategory(null);
      navigate(`/profile/${userId}`);
    }
  };

  const renderCategoryUserList = () => {
    if (!selectedCategory) return null;
    const { title, users } = selectedCategory;

    return (
      <StandardModal
        navigationParent={navigationParent ? `modal-${navigationParent}` : null}
        show={Boolean(selectedCategory)}
        handleClose={() => setSelectedCategory(null)}
        title={title}
        style={{ maxWidth: '480px', marginTop: '6vh' }}
        footerMeta={
          <span style={{ color: 'var(--foreground)', fontSize: '0.85rem' }}>
            Total: <strong>{users.length}</strong> {users.length === 1 ? 'user' : 'users'}
          </span>
        }
        ModalBodyComponent={
          <div className="p-1 p-sm-2" data-bs-theme={isDark ? 'dark' : 'light'}>
            <Card className="meetings-mock-card-shell p-2 p-sm-3 shadow-sm border-0">
              {users.length === 0 ? (
                <div className="text-center text-muted py-3" style={{ fontSize: '0.85rem' }}>No users found for this category.</div>
              ) : (
                <ListGroup variant="flush">
                  {users.map((u, idx) => {
                    const fullName = `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unnamed User';
                    const avatarSrc = resolveAvatarSrc(u.avatarUrl || u.avatar);
                    const initials = getUserInitials(u, 'U');
                    const acl = u.acl || 'Guest';

                    return (
                      <ListGroup.Item
                        key={u._id || idx}
                        action
                        onClick={() => handleUserClick(u)}
                        className="d-flex align-items-center justify-content-between py-2 px-2 border-bottom"
                        style={{ cursor: 'pointer', background: 'transparent', color: 'var(--foreground)' }}
                      >
                        <div className="d-flex align-items-center gap-2 overflow-hidden me-2">
                          {avatarSrc ? (
                            <Image
                              src={avatarSrc}
                              roundedCircle
                              width={32}
                              height={32}
                              alt={fullName}
                              style={{ objectFit: 'cover', flexShrink: 0 }}
                            />
                          ) : (
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white bg-primary flex-shrink-0"
                              style={{ width: 32, height: 32, fontSize: '0.75rem' }}
                            >
                              {initials}
                            </div>
                          )}
                          <div>
                            <div className="fw-semibold text-truncate" style={{ color: 'var(--foreground)', fontSize: '0.85rem' }}>
                              {fullName}
                            </div>
                          </div>
                        </div>
                        <Badge
                          bg={acl === 'Officer' ? 'warning' : acl === 'Member' ? 'primary' : 'secondary'}
                          pill
                          className="flex-shrink-0"
                          style={{ fontSize: '0.7rem' }}
                        >
                          {acl}
                        </Badge>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </Card>
          </div>
        }
        ModalFooterComponent={
          <Button variant="secondary" size="sm" onClick={() => setSelectedCategory(null)}>
            Close
          </Button>
        }
      />
    );
  };

  return (
    <>
      <StandardModal
        navigationParent={navigationParent}
        show={show}
        handleClose={handleClose}
        title="Users Stats"
        style={{ maxWidth: '960px', marginTop: '5vh' }}
        ModalBodyComponent={renderModalContent()}
        ModalFooterComponent={
          <Button variant={isDark ? 'secondary' : 'outline-dark'} onClick={handleClose}>
            Close
          </Button>
        }
      />
      {renderCategoryUserList()}
    </>
  );
}
